import { Immutable } from "immer";
import React from "react";
import { slugify } from "tsi-common-react/src/utils/text";
import { t } from "ttag";

import type { SealyFinanceFAQsBlock as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

/**
 * One FAQ row, owning its own accordion disclosure state. Toggling the
 * question button flips `accordion--is-active` and `aria-expanded`, and its
 * answer panel gains `accordion-target--is-active`, which opens it. Question
 * and answer are raw CMS HTML injected via `dangerouslySetInnerHTML`.
 */
const FAQItem = (
    props: Immutable<{
        question: string;
        answer: string;
        targetID: string;
        hidden: boolean;
    }>,
): React.JSX.Element => {
    const [isActive, setIsActive] = React.useState(false);
    return (
        <article
            className={concatClassNames([
                styles.financeFaq,
                props.hidden ? styles.financeFaqHidden : null,
            ])}
        >
            <h4>
                <button
                    className={concatClassNames([
                        styles.financeFaqQuestion,
                        "accordion",
                        isActive ? "accordion--is-active" : null,
                    ])}
                    aria-controls={props.targetID}
                    aria-expanded={isActive ? "true" : "false"}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsActive((active) => !active);
                    }}
                    dangerouslySetInnerHTML={{ __html: props.question }}
                />
            </h4>
            <EditorContent>
                {(marker) => (
                    <div
                        className={concatClassNames([
                            styles.financeFaqAnswer,
                            "accordion-target",
                            isActive ? "accordion-target--is-active" : null,
                        ])}
                        id={props.targetID}
                        dangerouslySetInnerHTML={{ __html: props.answer }}
                        {...marker}
                    />
                )}
            </EditorContent>
        </article>
    );
};

/**
 * The "Show All FAQs..." reveal is owned here via `showAll` state: it drops
 * `financeFaqHidden` from the overflow items and hides the button with
 * `financeFaqSeeMoreHidden`.
 *
 * The block renders as a bare fragment (no wrapping element). `counter` is
 * 1-indexed; items 6+ (counter > 5) start hidden, and the see-more button is
 * emitted once, right after the article whose counter == 6.
 */
export const SealyFinanceFAQsBlock = (props: Props): React.JSX.Element => {
    const faqs = props.value.faqs ?? [];
    const [showAll, setShowAll] = React.useState(false);
    return (
        <>
            {faqs.map((item, idx) => {
                const counter = idx + 1;
                const question = item.question ?? "";
                const answer = item.answer ?? "";
                // Reproduce Django `slugify(block.question)`. The question may
                // contain HTML, so strip tags first (Django renders it raw via
                // `|safe`, but slugify operates on the model value); the result
                // is not pixel-visible — it only needs to keep the
                // `aria-controls`/`id` pair linked. Compute once and reuse for
                // both so the accordion association holds.
                const slug = slugify(question.replace(/<[^>]*>/g, " "));
                const targetID = `faq-${slug}-${counter}`;
                return (
                    // Index-derived key: ListBlock items surface no stable id, so
                    // a CMS reorder reuses positional DOM — matches the legacy
                    // `forloop`-positional render.
                    <React.Fragment key={idx}>
                        <FAQItem
                            question={question}
                            answer={answer}
                            targetID={targetID}
                            hidden={counter > 5 && !showAll}
                        />
                        {counter === 6 && (
                            <button
                                className={concatClassNames([
                                    styles.financeFaqSeeMore,
                                    "al-financing-page__show-all-faqs",
                                    showAll
                                        ? styles.financeFaqSeeMoreHidden
                                        : null,
                                ])}
                                onClick={() => setShowAll(true)}
                            >
                                {t`Show All FAQs...`}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};
